import React, { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { isMobilePhone } from './detection/deviceDetection';

interface MobileRouteGuardProps {
  children: React.ReactNode;
}

/**
 * MobileRouteGuard - Wrapper component that auto-redirects phones to /mobile routes
 *
 * This component wraps desktop routes and automatically redirects mobile phone users
 * to the equivalent mobile route. Tablets are not redirected and use desktop routes.
 *
 * Preserves query parameters (e.g., spaceId) during redirect.
 *
 * @param {MobileRouteGuardProps} props - Component props
 * @param {React.ReactNode} props.children - The desktop route content to render for non-phone devices
 *
 * @example
 * <Route path="/studio">
 *   <MobileRouteGuard>
 *     <DesktopStudio />
 *   </MobileRouteGuard>
 * </Route>
 */
export function MobileRouteGuard({ children }: MobileRouteGuardProps) {
  const history = useHistory();
  const location = useLocation();
  const isPhone = isMobilePhone();

  useEffect(() => {
    if (isPhone) {
      // Get the current path
      const currentPath = location.pathname;

      // Only redirect if not already on a mobile route
      if (!currentPath.startsWith('/mobile')) {
        // Construct the mobile route path
        const mobilePath = `/mobile${currentPath}`;

        // Preserve query parameters and hash
        const search = location.search;
        const hash = location.hash;

        // Redirect to mobile route
        history.replace(`${mobilePath}${search}${hash}`);
      }
    }
  }, [isPhone, location, history]);

  // If this is a phone, don't render children (we're redirecting)
  if (isPhone) {
    return null;
  }

  // Render children for tablets and desktops
  return <>{children}</>;
}
